import type { FilterOperator, FilterState, FilterValue } from "./types";
import { isCustomFieldFilterField } from "./custom-field-filter-fields";
import { parseAssigneePrincipalValue } from "./composite-values";
import {
  isPresetValue,
  resolveDatePreset,
  type DatePresetValue,
} from "#app/lib/utils/date-presets";
import { at } from "#app/lib/utils/at";

// The field key of the Assignee filter — the PEOPLE doing the work. Synthetic
// because the server answers it through a nested ref (`assignees.entityId`)
// rather than a column of that name; see processFilter.
export const ASSIGNEE_PRINCIPAL_FIELD = "assignee";

/**
 * "Unassigned" as a member of the Assignee filter's id list — the one value
 * in it that is not a person. It reads as the server's own "no assignee row"
 * predicate (`assignees.entityId.isEmpty`), ORed with whoever else is picked:
 * a project is in the set when nobody has it OR one of them does. The key is
 * the group bucket the server files unassigned projects under, so the two
 * readings of "unassigned" share one spelling.
 */
export const UNASSIGNED_ASSIGNEE_ID = "__unassigned__";

/** The Assignee filter's list, split into its two questions. */
export function splitAssigneeValue(value: readonly string[]): {
  userIds: string[];
  unassigned: boolean;
} {
  return {
    userIds: value.filter((id) => id !== UNASSIGNED_ASSIGNEE_ID),
    unassigned: value.includes(UNASSIGNED_ASSIGNEE_ID),
  };
}

/** A RETIRED filter row: teams whose members are assigned. The panel no longer
 *  offers it — a team's work is what carries the team (`teamIds`, the Teams
 *  filter) — and stored rows read as Teams; see `migrateStoredFilterState`. */
export const ASSIGNEE_TEAM_FIELD = "assigneeTeamIds";

/**
 * Convert a "last updated" preset descriptor into a DateTimeFilter condition.
 * Resolves the preset to a {startDate, endDate} window and emits
 * { lastUpdateDate: { gte: startDate, lte: endDate } }.
 */
function convertLastUpdateDateFilter(
  preset: DatePresetValue
): Record<string, unknown> | null {
  if (preset.key === "custom" && !preset.custom) return null;
  const { startDate, endDate } = resolveDatePreset(preset.key, preset.custom);
  if (!startDate || !endDate) return null;
  return { lastUpdateDate: { gte: startDate, lte: endDate } };
}

// Operators that don't take a value
const NO_VALUE_OPERATORS: FilterOperator[] = [
  "isNull",
  "isNotNull",
  "isEmpty",
  "isNotEmpty",
];

// Operators that expect array values
const ARRAY_VALUE_OPERATORS: FilterOperator[] = [
  "in",
  "nin",
  "hasSome",
  "hasEvery",
];

/**
 * Build a GraphQL filter value from an operator and value.
 *
 * Translates logical operators like `isNull` into their Prisma equivalents
 * (e.g., `{ eq: null }`), and passes through standard operators directly.
 */
function buildFilterValue(
  operator: FilterOperator,
  value: unknown
): Record<string, unknown> | null {
  if (NO_VALUE_OPERATORS.includes(operator)) {
    switch (operator) {
      case "isNull":
        return { eq: null };
      case "isNotNull":
        return { ne: null };
      case "isEmpty":
        return { isEmpty: true };
      case "isNotEmpty":
        return { isEmpty: false };
      default:
        return null;
    }
  }

  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (ARRAY_VALUE_OPERATORS.includes(operator)) {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }
    return { [operator]: value };
  }

  return { [operator]: value };
}

/**
 * Build a nested filter structure from a dot-notation field path.
 *
 * e.g., "assignees.entityId" with `{ hasSome: ["id1"] }`
 * becomes `{ assignees: { entityId: { hasSome: ["id1"] } } }`
 */
function buildNestedFilter(
  fieldPath: string,
  filterValue: Record<string, unknown>
): Record<string, unknown> {
  const parts = fieldPath.split(".");

  if (parts.length === 1) {
    return { [fieldPath]: filterValue };
  }

  let result: Record<string, unknown> = filterValue;
  for (const part of [...parts].reverse()) {
    result = { [part]: result };
  }
  return result;
}

/**
 * Process a single FilterValue into a GraphQL filter object.
 */
function processFilter(
  filter: FilterValue
): Record<string, unknown> | null {
  const { field, operator, value } = filter;

  if (!field || !operator) {
    return null;
  }

  // Special case: "lastUpdateDate" quick-filter stores a preset descriptor
  // ({ key, custom? }) that resolves to a startDate/endDate range.
  if (field === "lastUpdateDate" && isPresetValue(value)) {
    return convertLastUpdateDateFilter(value);
  }

  // Custom-field rows (`values.<id>`) filter a jsonb value with no SQL NULL
  // to compare against — set/unset serialize to the server's `exists`
  // operator instead of the generic { eq: null } translation below.
  if (isCustomFieldFilterField(field) && (operator === "isNull" || operator === "isNotNull")) {
    return buildNestedFilter(field, { exists: operator === "isNotNull" });
  }

  // Special case: "workflow-stage" stores a composite { workflowIds, stageIds }
  // that must be decomposed into separate workflowId/stageId conditions.
  if (field === "workflow-stage" && value && typeof value === "object" && !Array.isArray(value)) {
    const { workflowIds, stageIds } = value as { workflowIds: string[]; stageIds: string[] };
    const conditions: Record<string, unknown>[] = [];
    if (workflowIds?.length) conditions.push({ workflowId: { in: workflowIds } });
    if (stageIds?.length) conditions.push({ stageId: { in: stageIds } });
    if (conditions.length === 0) return null;
    if (conditions.length === 1) return at(conditions, 0, "condition");
    return { AND: conditions };
  }

  // "assignee" is the PEOPLE assigned, and it filters a nested ref
  // (`assignees.entityId`) rather than a column of its own. The project's own
  // Teams property is `teamIds` — a different question.
  if (field === ASSIGNEE_PRINCIPAL_FIELD && Array.isArray(value)) {
    if (value.length === 0) return null;
    const { userIds, unassigned } = splitAssigneeValue(value as string[]);
    const conditions: Record<string, unknown>[] = [];
    // Two clauses, not one filter object: the server ANDs `isEmpty` and
    // `hasSome` when both ride the same ref, and nothing is both unowned and
    // owned by one of them.
    if (unassigned) {
      conditions.push(buildNestedFilter("assignees.entityId", { isEmpty: true }));
    }
    if (userIds.length > 0) {
      conditions.push(buildNestedFilter("assignees.entityId", { hasSome: userIds }));
    }
    if (conditions.length === 1) return at(conditions, 0, "condition");
    return { OR: conditions };
  }

  // Retired team shapes, for states that go straight to a query (a view's
  // saved filters on the board and the table) — read exactly as
  // `migrateStoredFilterState` rewrites them for the panel, so the query and
  // the panel never disagree: a team half is a Teams row, ANDed with the
  // people.
  if (field === ASSIGNEE_TEAM_FIELD) {
    return processFilter({ ...filter, field: "teamIds" });
  }
  if (field === ASSIGNEE_PRINCIPAL_FIELD && value && typeof value === "object" && !Array.isArray(value)) {
    const { userIds, teamIds } = parseAssigneePrincipalValue(value);
    const conditions: Record<string, unknown>[] = [];
    if (userIds.length > 0) {
      conditions.push(buildNestedFilter("assignees.entityId", { hasSome: userIds }));
    }
    if (teamIds.length > 0) conditions.push({ teamIds: { hasSome: teamIds } });
    if (conditions.length === 0) return null;
    if (conditions.length === 1) return at(conditions, 0, "condition");
    return { AND: conditions };
  }

  const filterValue = buildFilterValue(operator, value);
  if (!filterValue) {
    return null;
  }

  return buildNestedFilter(field, filterValue);
}

/**
 * Convert a FilterState into a GraphQL WhereInput object.
 *
 * Each block's filters are combined with the block's logic (AND/OR).
 * Multiple blocks are combined with the main logic operator.
 */
export function buildWhereFromState(
  state: FilterState
): Record<string, unknown> | undefined {
  if (!state.blocks.length) {
    return undefined;
  }

  const processedBlocks = state.blocks
    .map((block) => {
      const conditions = block.filters
        .map(processFilter)
        .filter(Boolean) as Record<string, unknown>[];

      if (conditions.length === 0) {
        return null;
      }

      if (conditions.length === 1) {
        return conditions[0];
      }

      const blockOp = block.logic === "OR" ? "OR" : "AND";
      return { [blockOp]: conditions };
    })
    .filter(Boolean) as Record<string, unknown>[];

  if (processedBlocks.length === 0) {
    return undefined;
  }

  if (processedBlocks.length === 1) {
    return processedBlocks[0];
  }

  const mainOp = state.logic === "OR" ? "OR" : "AND";
  return { [mainOp]: processedBlocks };
}

/**
 * The where for several FilterStates applied AT ONCE — the Simple and Advanced
 * editors' states, which are separate objects and both apply. States that
 * build no where drop out; one survivor is returned as itself, so a lone
 * state produces exactly what `buildWhereFromState` would.
 */
export function buildWhereFromStates(
  ...states: FilterState[]
): Record<string, unknown> | undefined {
  const wheres = states
    .map(buildWhereFromState)
    .filter((where): where is Record<string, unknown> => where !== undefined);
  if (wheres.length === 0) return undefined;
  if (wheres.length === 1) return wheres[0];
  return { AND: wheres };
}

/**
 * Count the total number of active filters in a FilterState.
 */
export function countActiveFilters(state: FilterState): number {
  let count = 0;
  for (const block of state.blocks) {
    for (const filter of block.filters) {
      if (!filter.field || !filter.operator) continue;
      if (NO_VALUE_OPERATORS.includes(filter.operator)) {
        count++;
      } else if (filter.value !== null && filter.value !== undefined && filter.value !== "") {
        if (Array.isArray(filter.value)) {
          if (filter.value.length > 0) count++;
        } else if (filter.field === "workflow-stage" && typeof filter.value === "object") {
          const { workflowIds, stageIds } = filter.value as { workflowIds: string[]; stageIds: string[] };
          if (workflowIds?.length || stageIds?.length) count++;
        } else if (
          filter.field === ASSIGNEE_PRINCIPAL_FIELD &&
          typeof filter.value === "object"
        ) {
          const { userIds, teamIds } = filter.value as { userIds: string[]; teamIds: string[] };
          if (userIds?.length || teamIds?.length) count++;
        } else {
          count++;
        }
      }
    }
  }
  return count;
}
